import { getMinLengthError, ValidationError } from '@lender/utils';
import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { checkIsRetainedPropertyStatus } from 'app/pages/loanApplication/LoanApplication.utils';
import { compose } from 'redux';
import { mixed } from 'yup';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  attachmentTypeOptions,
  deedRestrictedOptions,
  locationTypeOptions,
  manufacturedHomeTypeOptions,
  numberOfUnitsOptions,
  occupancyOptions,
  propertyRightsEstateHeldOptions,
  propertyStatusOptions,
  propertyTypeOptions,
} from 'constants/options';
import { checkIsTexasState } from 'constants/states';
import { EMPLOYER_IDENTIFICATION_NUMBER_FORMAT } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { proposedPropertyExpensesFields } from '../PropertyExpenses/PropertyExpenses.config';

const EIN_LENGTH = 9;

export const propertyConfigFields = {
  appraisedValue: {
    fieldId: `appraisedValue`,
    type: FieldType.INPUT,
    nameInModel: 'appraisedValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Appraised Value',
      label: 'Appraised Value',
      required: false,
      decimalScale: 0,
      variant: 'outlined',
    },
  },
  propertyValue: {
    fieldId: `propertyValue`,
    type: FieldType.INPUT,
    nameInModel: 'propertyValue',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Purchase Price',
      label: 'Purchase Price',
      required: true,
      disabled: false,
      decimalScale: 0,
      variant: 'outlined',
    },
  },
  totalSalesConcessionAmount: {
    fieldId: `totalSalesConcessionAmount`,
    type: FieldType.INPUT,
    nameInModel: 'totalSalesConcessionAmount',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Seller Concessions',
      label: 'Seller Concessions',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_SELLER_CONCESSIONS_INPUT,
      disabled: false,
      required: false,
      minValue: 0,
      variant: 'outlined',
    },
  },
  occupancy: {
    fieldId: `occupancy`,
    type: FieldType.SELECT,
    nameInModel: 'occupancy',
    inputProps: {
      options: occupancyOptions,
      placeholder: 'Occupancy',
      label: 'Occupancy',
      required: true,
      disabled: false,
      variant: 'outlined',
    },
  },
  isRelocationLoan: {
    fieldId: 'isRelocationLoan',
    type: FieldType.SWITCH,
    nameInModel: 'isRelocationLoan',
    inputProps: {
      label: 'Relocation Loan:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_RELOCATION_LOAN_SWITCH,
      disabled: false,
      required: false,
    },
  },
  isShortTermRental: {
    fieldId: 'isShortTermRental',
    type: FieldType.SWITCH,
    nameInModel: 'isShortTermRental',
    inputProps: {
      label: 'Short-Term Rental:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_SHORT_TERM_RENTAL_SWITCH,
      disabled: false,
      required: false,
    },
  },
  isCondotel: {
    fieldId: 'isCondotel',
    type: FieldType.SWITCH,
    nameInModel: 'isCondotel',
    inputProps: {
      label: 'Condotel:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_CONDOTEL_SWITCH,
      disabled: false,
      required: false,
    },
  },
  locationType: {
    fieldId: 'locationType',
    type: FieldType.SELECT,
    nameInModel: 'locationType',
    inputProps: {
      options: locationTypeOptions,
      placeholder: 'Location',
      label: 'Location',
      required: false,
      disabled: false,
      variant: 'outlined',
      withSortedOptions: false,
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_LOCATION_SELECT,
    },
  },
  propertyType: {
    fieldId: `propertyType`,
    type: FieldType.SELECT,
    nameInModel: 'propertyType',
    inputProps: {
      options: propertyTypeOptions,
      placeholder: 'Property Type',
      label: 'Property Type',
      required: true,
      disabled: false,
      variant: 'outlined',
    },
  },
  attachmentType: {
    fieldId: `attachmentType`,
    type: FieldType.SELECT,
    nameInModel: 'attachmentType',
    inputProps: {
      options: attachmentTypeOptions,
      placeholder: 'Attachment',
      label: 'Attachment',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  isDeedRestricted: {
    fieldId: `isDeedRestricted`,
    type: FieldType.SWITCH,
    nameInModel: 'isDeedRestricted',
    inputProps: {
      label: 'Deed Restricted:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_DEED_RESTRICTED_CHECKBOX,
      disabled: false,
      required: false,
    },
  },
  deedRestrictionType: {
    fieldId: `deedRestrictionType`,
    type: FieldType.SELECT,
    nameInModel: 'deedRestrictionType',
    inputProps: {
      options: deedRestrictedOptions,
      placeholder: 'Type',
      label: 'Type',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  deedRestrictionMonths: {
    fieldId: `deedRestrictionMonths`,
    type: FieldType.INPUT,
    nameInModel: 'deedRestrictionMonths',
    inputProps: {
      placeholder: 'Months',
      label: 'Months',
      disabled: false,
      required: true,
      variant: 'outlined',
      type: FieldInputType.INT,
    },
  },
  isNewConstruction: {
    fieldId: `isNewConstruction`,
    type: FieldType.SWITCH,
    nameInModel: 'isNewConstruction',
    inputProps: {
      label: 'New Construction:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_NEW_CONSTRUCTION_CHECKBOX,
      disabled: false,
      required: false,
    },
  },
  hasOweltyOfPartition: {
    fieldId: `hasOweltyOfPartition`,
    type: FieldType.SWITCH,
    nameInModel: 'hasOweltyOfPartition',
    inputProps: {
      label: 'Owelty of Partition:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_OWELTY_OF_PARTITION_CHECKBOX,
      disabled: false,
      required: false,
    },
  },
  mixedUseProperty: {
    fieldId: 'mixedUseProperty',
    type: FieldType.SWITCH,
    nameInModel: 'mixedUseProperty',
    inputProps: {
      label: 'Mixed-Use Property:',
      disabled: false,
    },
  },
  numberOfUnits: {
    fieldId: `numberOfUnits`,
    type: FieldType.SELECT,
    nameInModel: 'numberOfUnits',
    inputProps: {
      placeholder: 'Number Of Units',
      label: 'Number Of Units',
      disabled: false,
      required: true,
      variant: 'outlined',
      options: numberOfUnitsOptions,
      withSortedOptions: false,
    },
  },
  propertyRightsEstateHeld: {
    fieldId: `propertyRightsEstateHeld`,
    type: FieldType.SELECT,
    nameInModel: 'propertyRightsEstateHeld',
    inputProps: {
      placeholder: 'Property Rights / Estate Held',
      label: 'Property Rights / Estate Held',
      disabled: false,
      variant: 'outlined',
      options: propertyRightsEstateHeldOptions,
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_RIGHTS_ESTATE_SELECT,
    },
  },
  leaseholdExpiration: {
    fieldId: `leaseholdExpiration`,
    type: FieldType.DATE,
    nameInModel: 'leaseholdExpiration',
    inputProps: {
      type: FieldInputType.DATE,
      label: 'Leasehold Expiration',
      required: false,
      disabled: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_LEASEHOLD_EXPIRATION_INPUT,
    },
  },
  status: {
    fieldId: `status`,
    type: FieldType.SELECT,
    nameInModel: 'status',
    inputProps: {
      options: propertyStatusOptions,
      placeholder: 'Property Status',
      label: 'Property Status',
      disabled: false,
      required: true,
      variant: 'outlined',
    },
  },
  manufacturedHomeType: {
    fieldId: `manufacturedHomeType`,
    type: FieldType.SELECT,
    nameInModel: 'manufacturedHomeType',
    inputProps: {
      options: manufacturedHomeTypeOptions,
      placeholder: 'Manufactured Home Type',
      label: 'Manufactured Home Type',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_TYPE_OF_MANUFACTURED_HOME_SELECT,
    },
  },
  completionDate: {
    fieldId: `completionDate`,
    type: FieldType.DATE,
    nameInModel: 'completionDate',
    inputProps: {
      format: COMMON_DATE_FORMAT,
      label: 'Completion Date',
      disabled: false,
      required: true,
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_COMPLETION_DATE_INPUT,
    },
  },
  originalCost: {
    fieldId: 'originalCost',
    type: FieldType.INPUT,
    nameInModel: 'originalCost',
    inputProps: {
      type: FieldInputType.CURRENCY,
      placeholder: 'Original Cost',
      label: 'Original Cost',
      required: false,
      disabled: false,
      decimalScale: 0,
      variant: 'outlined',
    },
  },
  hasAffordableLegalRestrictions: {
    fieldId: 'hasAffordableLegalRestrictions',
    type: FieldType.SWITCH,
    nameInModel: 'hasAffordableLegalRestrictions',
    inputProps: {
      label: 'Affordable Legal Restrictions:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_AFFORDABLE_LEGAL_RESTRICTIONS_CHECKBOX,
      disabled: false,
      required: false,
    },
  },
  isConveyable: {
    fieldId: 'isConveyable',
    type: FieldType.SWITCH,
    nameInModel: 'isConveyable',
    inputProps: {
      label: 'Is Conveyable:',
      dataTestId: PropertyAndLoanTestIds.PROPERTY_FORM_IS_CONVEYABLE_CHECKBOX,
      disabled: false,
      required: false,
    },
  },
  affordableHomeProviderEin: {
    fieldId: 'affordableHomeProviderEin',
    type: FieldType.INPUT,
    nameInModel: 'affordableHomeProviderEin',
    inputProps: {
      type: FieldInputType.TEXT_SPECIFIC_PATTERN,
      placeholder: 'Affordable Home Provider EIN',
      label: 'Affordable Home Provider EIN',
      disabled: false,
      required: false,
      format: EMPLOYER_IDENTIFICATION_NUMBER_FORMAT,
      mask: '_',
      allowNegative: false,
      isNumericString: true,
      patternRegExp: /[0-9]{9}/,
      validationMessage: getMinLengthError(EIN_LENGTH),
      allowLeadingZeros: true,
      variant: 'outlined',
    },
  },
  communityLandTrustEin: {
    fieldId: 'communityLandTrustEin',
    type: FieldType.INPUT,
    nameInModel: 'communityLandTrustEin',
    inputProps: {
      type: FieldInputType.TEXT_SPECIFIC_PATTERN,
      placeholder: 'Community Land Trust EIN',
      label: 'Community Land Trust EIN',
      disabled: false,
      required: false,
      format: EMPLOYER_IDENTIFICATION_NUMBER_FORMAT,
      mask: '_',
      allowNegative: false,
      isNumericString: true,
      patternRegExp: /[0-9]{9}/,
      validationMessage: getMinLengthError(EIN_LENGTH),
      allowLeadingZeros: true,
      variant: 'outlined',
    },
  },
  countyFipsCode: {
    fieldId: 'countyFipsCode',
    type: FieldType.INPUT,
    nameInModel: 'countyFipsCode',
    inputProps: {
      placeholder: 'FIPS Code',
      label: 'FIPS Code',
      disabled: true,
      required: false,
      variant: 'outlined',
    },
  },
};

const addressFieldsKeys = ['street', 'unit', 'city', 'county', 'state', 'zip'];
export const propertySectionFieldsKeys = [
  ...Object.keys(propertyConfigFields),
  ...addressFieldsKeys,
  'reapiPropertyId',
];

export const getPropertyConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    addressCounties,
    isMilitaryAddressAllowed,
    isPurchase,
    isDeedRestricted,
    isConstructionToPermanent,
    isRefinance,
    isAdditionalLoan,
    isHelocPurposePurchase,
    isMultiUnit,
    isLeasehold,
    isApplicationTbd,
    isSubjectProperty,
    onChangeAddressState,
    state,
    isMixedUseVisible,
    isManufacturedHome,
    isCompletionDateVisible,
    isFha,
    hasAffordableLegalRestrictions,
    isCondo,
  }) => {
    return {
      groups: [
        {
          type: FieldType.GROUP,
          fields: [
            ...Object.values(
              getAddressFieldsConfig({
                prefix: '',
                isWithCountry: false,
                isStateDisabled: !isApplicationTbd && !isSubjectProperty,
                isCountyRequired: true,
                isStatesWithMilitaryAddresses: isMilitaryAddressAllowed,
                filteredCounties: addressCounties,
                onChangeAddressState,
              }),
            ),
            propertyConfigFields.countyFipsCode,
          ],
          key: `property-address`,
          lockAsGroup: true,
          groupTitle: {
            label: 'Property Address',
          },
        },
        {
          type: FieldType.GROUP,
          fields: [
            propertyConfigFields.isDeedRestricted,
            ...getConditionalArray(isDeedRestricted, [
              propertyConfigFields.deedRestrictionType,
              propertyConfigFields.deedRestrictionMonths,
            ]),
          ],
          key: `deed-restricted`,
          lockAsGroup: true,
        },
        ...getConditionalArray(isPurchase || (isRefinance && isConstructionToPermanent), () => [
          {
            type: FieldType.GROUP,
            fields: [
              propertyConfigFields.isNewConstruction,
              ...getConditionalArray(isCompletionDateVisible, [
                propertyConfigFields.completionDate,
              ]),
            ],
            key: `new-construction`,
            lockAsGroup: true,
          },
        ]),
        ...getConditionalArray(isMixedUseVisible, [
          {
            type: FieldType.GROUP,
            fields: [propertyConfigFields.mixedUseProperty],
            key: `mixed-use-property`,
          },
        ]),
        ...getConditionalArray(checkIsTexasState(state), [
          {
            type: FieldType.GROUP,
            fields: [propertyConfigFields.hasOweltyOfPartition],
            key: `owelty-of-partition`,
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            propertyConfigFields.appraisedValue,
            propertyConfigFields.attachmentType,
            ...getConditionalArray(isLeasehold, [
              {
                ...propertyConfigFields.leaseholdExpiration,
                inputProps: {
                  ...propertyConfigFields.leaseholdExpiration.inputProps,
                  required: isLeasehold,
                },
              },
            ]),
            ...getConditionalArray(isMultiUnit, [propertyConfigFields.numberOfUnits]),
            propertyConfigFields.occupancy,
            ...getConditionalArray(isPurchase, [propertyConfigFields.isRelocationLoan]),
            propertyConfigFields.propertyRightsEstateHeld,
            propertyConfigFields.propertyType,
            ...getConditionalArray(isCondo, [propertyConfigFields.isCondotel]),
            ...getConditionalArray(isManufacturedHome, [propertyConfigFields.manufacturedHomeType]),
            ...getConditionalArray(isPurchase, [propertyConfigFields.propertyValue]),
            ...getConditionalArray(isRefinance, [
              {
                ...propertyConfigFields.status,
                inputProps: {
                  ...propertyConfigFields.status.inputProps,
                  customSchema: () =>
                    mixed()
                      .required(ValidationError.RequiredField)
                      .test(
                        'status-retained-check',
                        "You cannot refinance a property that is for sale. Please select 'Retained' to continue.",
                        ({ value } = {}) => checkIsRetainedPropertyStatus(value),
                      ),
                },
              },
            ]),
            ...getConditionalArray(isRefinance || (isAdditionalLoan && !isHelocPurposePurchase), [
              propertyConfigFields.originalCost,
            ]),
            ...getConditionalArray(isPurchase, [propertyConfigFields.totalSalesConcessionAmount]),
            propertyConfigFields.locationType,
            propertyConfigFields.isShortTermRental,
          ],
          key: `property-and-occupancy-info`,
          groupTitle: {
            label: 'Property & Occupancy',
          },
        },
        ...getConditionalArray(isFha, () => [
          {
            type: FieldType.GROUP,
            fields: [
              propertyConfigFields.hasAffordableLegalRestrictions,
              ...getConditionalArray(hasAffordableLegalRestrictions, [
                propertyConfigFields.isConveyable,
              ]),
              propertyConfigFields.affordableHomeProviderEin,
              propertyConfigFields.communityLandTrustEin,
            ],
            key: 'affordable-housing',
            lockAsGroup: true,
            groupTitle: {
              label: 'Affordable Housing',
            },
          },
        ]),
        {
          type: FieldType.GROUP,
          fields: [
            proposedPropertyExpensesFields.expenses,
            proposedPropertyExpensesFields.netMonthlyRentalIncome,
          ],
          title: { className: 'tool__visually-hidden' },
        },
      ],
    };
  },
);
